import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { restoreCatalogItem } from '@/api/catalog-recovery';
import { toast } from '@/hooks/use-toast';
import { VariantStockSearchItem } from '@/types/product-type';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Props {
  mode?: 'products' | 'variants';
  items: VariantStockSearchItem[];
  isLoading: boolean;
  searchQuery: string;
  onSearch: (value: string) => void;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  onRefresh: () => void;
}

export function CatalogRecoveryTable({ mode = 'products', items, isLoading, searchQuery, onSearch, page, totalPages, setPage, onRefresh }: Props) {
  const { t } = useTranslation('product');
  const [restoring, setRestoring] = useState<string | null>(null);
  const restore = async (item: VariantStockSearchItem, type: 'product' | 'variant') => {
    const id = type === 'product' ? item.productId : String(item.id);
    if (!window.confirm(t(type === 'product' ? 'recovery.confirm' : 'recovery.confirmVariant'))) return;
    setRestoring(id);
    try {
      await restoreCatalogItem(type, id);
      onRefresh();
    } catch (error) {
      toast({ title: t('recovery.failed', { defaultValue: 'Restore failed' }), description: error instanceof Error ? error.message : undefined, variant: 'destructive' });
    } finally {
      setRestoring(null);
    }
  };

  const groups = new Map<string, VariantStockSearchItem[]>();
  items.forEach((item) => groups.set(item.productId, [...(groups.get(item.productId) || []), item]));
  const rows: { item: VariantStockSearchItem; type: 'product' | 'variant'; canRestore: boolean }[] = mode === 'variants'
    ? items.filter((item) => item.id != null).map((item) => ({ item, type: 'variant', canRestore: Boolean(item.productDeletedAt || item.variantDeletedAt) }))
    : Array.from(groups.values()).flatMap((group) => [
      { item: group[0], type: 'product' as const, canRestore: group.some((item) => Boolean(item.productDeletedAt || item.variantDeletedAt)) },
      ...group.filter((item) => item.id != null).map((item) => ({ item, type: 'variant' as const, canRestore: Boolean(item.productDeletedAt || item.variantDeletedAt) })),
    ]);

  return <div className="space-y-3 rounded-lg border bg-background p-4">
    <Input aria-label={t('recovery.search')} placeholder={t('recovery.search')} value={searchQuery} onChange={(event) => onSearch(event.target.value)} />
    <div className="overflow-x-auto"><Table>
      <TableHeader><TableRow>
        <TableHead>{t('recovery.product')}</TableHead><TableHead>{t('recovery.variant')}</TableHead>
        <TableHead>{t('recovery.status')}</TableHead><TableHead>{t('recovery.actions')}</TableHead>
      </TableRow></TableHeader>
      <TableBody>{isLoading ? <TableRow><TableCell colSpan={4}>{t('recovery.loading')}</TableCell></TableRow> : items.length === 0 ?
        <TableRow><TableCell colSpan={4}>{t('recovery.empty')}</TableCell></TableRow> : rows.map(({ item, type, canRestore }) => {
          const deleted = Boolean(item.productDeletedAt || (type === 'variant' && item.variantDeletedAt));
          return <TableRow key={`${type}-${type === 'product' ? item.productId : item.id}`} className={type === 'product' ? 'bg-muted/50 font-medium' : undefined}>
            <TableCell>{type === 'product' || mode === 'variants' ? <>{item.productName}<div className="text-muted-foreground">{item.productCode || item.saleCode}</div></> : null}</TableCell>
            <TableCell>{type === 'variant' ? <div className={mode === 'products' ? 'pl-4' : undefined}>{item.variantName}<div className="text-muted-foreground">{item.sku}</div></div> : null}</TableCell>
            <TableCell><Badge variant={deleted ? 'destructive' : 'secondary'}>{t(deleted ? 'recovery.deleted' : 'recovery.available')}</Badge>
              {type === 'variant' && item.productDeletedAt && <div className="text-xs text-muted-foreground">{t('recovery.productDeleted')}</div>}
            </TableCell>
            <TableCell>{canRestore ?
              <Button size="sm" variant="outline" disabled={restoring !== null} onClick={() => restore(item, type)}>{t(type === 'product' ? 'recovery.restoreProduct' : 'recovery.restoreVariant')}</Button> :
              <Button size="sm" variant="outline" asChild><a href={`/products/${item.productId}/edit`}>{t('recovery.open')}</a></Button>}</TableCell>
          </TableRow>;
        })}</TableBody>
    </Table></div>
    <div className="flex items-center justify-end gap-3">
      <Button variant="outline" disabled={isLoading || page === 0} onClick={() => setPage(page - 1)}>{t('recovery.previous')}</Button>
      <span>{page + 1} / {Math.max(1, totalPages)}</span>
      <Button variant="outline" disabled={isLoading || page + 1 >= totalPages} onClick={() => setPage(page + 1)}>{t('recovery.next')}</Button>
    </div>
  </div>;
}
